import {useTranslation} from 'react-i18next';
import {Modal, ModalProps} from '../modal/modal';
import {translations} from '../../i18n/translations';
import {Stack, SxProps, Typography} from '@mui/material';
import {useAppContext} from '../../contexts/app/context';
import {ThemeMode} from '../../enums';
import {Pallete} from '../../theme/bluebird';

interface DeleteResourceConfirmationModalProps extends Omit<ModalProps, 'onClose' | 'actions'> {
  title: string;
  description: string;
  onCancel: () => void;
  onConfirm: () => void;
  cannotBeUndoneMessage?: string;
  isDeleting?: boolean;
  cancel_label?: string;
  confirm_label?: string;
}

const DeleteResourceConfirmationModal = ({
  description,
  onCancel,
  onConfirm,
  cannotBeUndoneMessage,
  isDeleting,
  cancel_label,
  confirm_label,
  ...rest
}: DeleteResourceConfirmationModalProps) => {
  const {t} = useTranslation();
  const appContext = useAppContext();

  const contentWrapperSx: SxProps = {
    alignItems: 'center',
    gap: '24px',
    margin: '30px 0',
  };

  const descriptionSx: SxProps = {
    fontWeight: '500',
    fontSize: '16px',
    lineHeight: '150%',
    textAlign: 'center',
    maxWidth: '356px',
    color:
      appContext.state.currentThemeMode === ThemeMode.Light
        ? Pallete.gray.base
        : Pallete.white.base,
  };

  const cannotBeUndoneSx: SxProps = {
    fontWeight: '500',
    fontSize: '14px',
    lineHeight: '150%',
    textAlign: 'center',
    maxWidth: '370px',
    color:
      appContext.state.currentThemeMode === ThemeMode.Light
        ? Pallete.gray.general
        : Pallete.gray.light,
  };

  return (
    <Modal
      {...rest}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      preventCloseClick
      actions={[
        {
          label: cancel_label || t(translations.modal.cancel),
          type: 'destructive',
          onClick: onCancel,
          disabled: isDeleting,
        },
        {
          label: confirm_label || t(translations.modal.confirm),
          type: 'nondestructive',
          onClick: onConfirm,
          loading: isDeleting,
        },
      ]}
    >
      <Stack sx={contentWrapperSx}>
        <Typography component="h3" className="description" sx={descriptionSx}>
          {description}
        </Typography>
        {cannotBeUndoneMessage && (
          <Typography component="h5" sx={cannotBeUndoneSx}>
            {cannotBeUndoneMessage}
          </Typography>
        )}
      </Stack>
    </Modal>
  );
};

export default DeleteResourceConfirmationModal;
